import * as THREE from "three"
import { useGLTF, useTexture } from '@react-three/drei'
import { Canvas, useFrame, useThree } from 'react-three-fiber'
import { Physics, useBox, useSphere } from "@react-three/cannon";
import create from "zustand"

import * as Tone from "tone"
import React, { Suspense, useRef, useCallback, useEffect, useMemo, useState } from "react"
import lerp from "lerp"
import clamp from "lodash-es/clamp"
import niceColors from 'nice-color-palettes'
import { TweenMax, Sine, gsap } from "gsap"
import inst from "./inst"

let loopCount = 0
let hitCount = 0

gsap.defaults({
  ease: Sine.easeOut
})
const col = 18
let movement = window.innerWidth < 800 ? 2.4 : 1.4
let noteLen = '8'
const timeGone = (inDate) => (new Date().getTime() - inDate.getTime()) / 1000;
let lampIntensity = (velocity) => Math.max(Math.min(velocity * 0.4, 3), 0.4)
let getInst = () => {
  hitCount++
  return (hitCount % 2 === 0) ? inst.synthL : inst.synthR
}
const [useStore] = create((set, get) => ({
  startTime: null,
  boxPos: [0, 0, 0],
  ballPos: [0, -200, 0],
  started: false,
  needsUpdate: false,
  box: null,
  sequence: [],
  parts: [],
  partsUI: null,
  mutation: { x: 0, y: 0, s: 0, b: 0.00001, mouseDown: false, models: [], spots: [] },
  currSynth: null,
  instIndex: 0,
  api: {
    getMut: (key) => { return get().mutation[key] },
    getFreq: (freq, octave) => {
      for (var i = 1; i < Math.abs(octave); i++) {
        octave > 0 ? freq = freq * 2 : freq = freq / 2
      }
      return freq
    },
    start: () => {
      Tone.Transport.start()
      set(state => ({ ballPos: state.boxPos }))
      get().started = true
      get().parts[0] && get().parts[0].part.stop()
    },
    stop: (check) => {
      if (check && !get().started) return
      if (!get().startTime) return
      let mut = get().mutation
      let quantTime = Tone.Time('1m').toSeconds() - (timeGone(get().startTime) % Tone.Time('1m').toSeconds())
      let loopEnd = timeGone(get().startTime) + quantTime
      get().started = false
      get().sequence.map((val) => val.loopEnd = loopEnd)
      if (get().sequence.length > 0) {
        let part = new Tone.Part(function (time, value) {
          let freq = get().api.getFreq(value.freq, get().parts[0].octave)
          let s = getInst()
          s.triggerAttackRelease(freq, noteLen, time, Math.min(1, value.velocity));
          mut.spots.forEach((spot) => spot.intensity = lampIntensity(value.velocity))
          get().api.pong(value.velocity / 3, value.side, true)
          if (value.hitIndex === 0) {
            loopCount++;
            if (loopCount % 8 === 0) {
              if (Math.random() > 0.5) {
                inst.delayL.wet.linearRampTo(Math.random() * 0.5, 1)
                inst.delayL.delayTime.linearRampTo(Math.random() * 4, 4)
                inst.delayL.feedback.linearRampTo(Math.random() * 0.5, 2)
                inst.filterHighL.frequency.linearRampTo(100 + (Math.random() * 800), 4)
                inst.filterLowL.frequency.linearRampTo(1200 - (Math.random() * 700), 4)
              }

              if (Math.random() > 0.5) {
                inst.delayR.wet.linearRampTo(Math.random() * 0.5, 1)
                inst.delayR.delayTime.linearRampTo(Math.random() * 4, 4)
                inst.delayR.feedback.linearRampTo(Math.random() * 0.5, 2)
                inst.filterHighR.frequency.linearRampTo(100 + (Math.random() * 800), 4)
                inst.filterLowR.frequency.linearRampTo(1200 - (Math.random() * 700), 4)
              }
              if (Math.random() > 0.75) {
                mut.models.forEach((model) => {
                  let s = 25
                  let s2 = 5
                  model.api.angularVelocity.set((-s * 0.5) + Math.random() * s, (-s * 0.5) + Math.random() * s, 0)
                  model.api.velocity.set((-s2 * 0.5) + Math.random() * s2, (-s2 * 0.5) + Math.random() * s2, 0)
                })
              }
            }
            //get().api.animateProgress(value.index, value.loopEnd)
          }
        }, get().sequence)
        part.loop = true;
        part.loopEnd = loopEnd
        part.start('+' + quantTime)


        get().parts[0] = {
          part,
          sequence: get().sequence,
          loopEnd: part.loopEnd,
          active: true,
          index: get().parts.length,
          octave: 1,
          instIndex: get().instIndex
        }
      }
      get().sequence = []
    },
    setInstIndex: (index) => set(() => ({ instIndex: index })),
    pong(velocity, side, bypass) {
      if (!get().started && !bypass) return
      const mut = get().mutation
      const to = clamp(velocity / 50, 0.05, 2)
      mut.spots.forEach((spot) => spot.intensity = lampIntensity(velocity))
      const toTime = 0.05;
      const backTime = 0.175;
      if (get().sequence.length === 0) {
        get().startTime = new Date();
      }

      if (side === 0) {
        TweenMax.to(mut, toTime, { y: to })
      } else if (side === 1) {
        TweenMax.to(mut, toTime, { x: to })
      } else if (side === 2) {
        TweenMax.to(mut, toTime, { x: -to })
      } else if (side === 3) {
        TweenMax.to(mut, toTime, { y: -to })
      }
      TweenMax.to(mut, backTime, { y: 0, x: 0, delay: toTime })

      TweenMax.to(mut, toTime, { s: to * 3 })
      TweenMax.to(mut, backTime * 12, { s: 0, delay: toTime })

      if (!bypass && velocity > 0.1) {
        let tempFreq = Math.max((velocity * 85), 60)
        let freq = get().api.getFreq(tempFreq, 0)
        let s = getInst()
        s.triggerAttackRelease(freq, noteLen, '+0.01', Math.min(1, velocity));
        get().sequence.push({
          time: timeGone(get().startTime),
          freq,
          side,
          velocity,
          index: get().parts.length,
          hitIndex: get().sequence.length
        })
      }
    },
    remove: (i) => {
      const part = get().parts[i]
      part.active = false
      part.part.stop()
      get().started = false
      get().needsUpdate = !get().needsUpdate
    },
    octaveUp: (i) => {
      get().parts[i].octave++
      if (get().parts[i].octave === 0 || get().parts[i].octave === -1) get().parts[i].octave = 1;
    },
    octaveDown: (i) => {
      get().parts[i].octave--
      if (get().parts[i].octave === 0) get().parts[i].octave = -2;
    },
  },
}))

let centerNode = (nodes, key) => {
  var center = new THREE.Vector3();
  let node = nodes[key]
  node.geometry.computeBoundingBox();
  node.geometry.boundingBox.getCenter(center);
  node.geometry.center();
  node.position.copy(center);
  return node
}

let ballSize = 0.7
let ballMass = 0.1

function Globe(props) {
  const { nodes } = useGLTF("./globe.gltf");
  const mutation = useStore(state => state.mutation)
  const [ref, api] = useSphere(() => ({ mass: ballMass, args: ballSize * 0.95, ...props }))
  useEffect(() => {
    mutation.models.push({ ref, api, scale: props.scale })
  }, [api])
  const node = useMemo(() => centerNode(nodes, 'node_MeshObject-931493888-PolyPaper21'), [nodes]);
  return (
    <mesh
      castShadow
      receiveShadow
      ref={ref}
      geometry={node.geometry}
      material={node.material}
      {...props}
      scale={[props.scale, props.scale, props.scale]}
    >
    </mesh>
  );
}

function Plant(props) {
  const { nodes } = useGLTF("./model.gltf");
  const node = useMemo(() => centerNode(nodes, 'node_MeshObject-1927842816-PolyPaper19'), [nodes]);

  const mutation = useStore(state => state.mutation)
  const [ref, api] = useSphere(() => ({ mass: ballMass, args: ballSize, ...props }))
  useEffect(() => {
    mutation.models.push({ ref, api, scale: props.scale })
  }, [api])
  return (
    <mesh
      castShadow
      receiveShadow
      ref={ref}
      geometry={node.geometry}
      material={node.material}
      {...props}
      scale={[props.scale, props.scale, props.scale]}
    >
    </mesh>
  );
}

function Bananas(props) {
  const { nodes } = useGLTF("./bananas.gltf");
  const node = useMemo(() => centerNode(nodes, 'node_MeshObject-963557376-PolyPaper0'), [nodes]);

  const mutation = useStore(state => state.mutation)
  const [ref, api] = useSphere(() => ({ mass: ballMass, args: ballSize * 0.7, ...props }))
  useEffect(() => {
    mutation.models.push({ ref, api, scale: props.scale })
  }, [api])
  return (
    <mesh
      castShadow
      receiveShadow
      ref={ref}
      geometry={node.geometry}
      material={node.material}
      {...props}
      scale={[props.scale, props.scale, props.scale]}
    >
    </mesh>
  );
}

function Lamp(props) {
  const mutation = useStore(state => state.mutation)
  const [ref, api] = useSphere(() => ({ mass: ballMass, args: ballSize, ...props }))
  useEffect(() => {
    mutation.models.push({ ref, api, scale: props.scale })
  }, [api])
  const { nodes } = useGLTF("./Desk_lamp_01.gltf");
  const light = useMemo(() => {
    let spot = new THREE.SpotLight()
    spot.color = new THREE.Color(0x00ffff)
    spot.castShadow = true
    spot.angle = 1.1
    spot.intensity = 1
    spot.distance = 6
    spot.penumbra = 0
    mutation.spots.push(spot)
    return spot
  }, [])


  const target = useRef();
  const node = useMemo(() => {
    var center = new THREE.Vector3();
    let node = nodes['buffer-0-mesh-0']
    node.geometry.computeBoundingBox();
    node.geometry.boundingBox.getCenter(center);
    node.geometry.center();
    node.position.copy(center);
    return node
  }, [nodes]);
  return (
    <group
      ref={ref}
      {...props}
      scale={[props.scale, props.scale, props.scale]}
    >
      <mesh
        castShadow
        receiveShadow
        geometry={node.geometry}
        material={new THREE.MeshStandardMaterial({ color: '#ee3344' })}
      />
      <mesh
        receiveShadow
        position={[0, 2, 1]}
        geometry={new THREE.SphereBufferGeometry(0.75, 6, 6)}
        material={new THREE.MeshStandardMaterial({ color: '#eee', transparent: true, opacity: 0.95 })}
      />

      <primitive object={light} position={[1, 1, 1]} roation={[...props.rotation]} />
      <primitive object={light.target} position={[-2, -2, 5]} />

    </group>
  );
}


let backSpeed = 0.2
let toSpeed = 0.2
function InsideBox({ ...props }) {
  const { getMut } = useStore(state => state.api)
  let values = useRef([0, 0])
  let box = useRef()
  const back = -0.5
  const back2 = -2
  useFrame(state => {
    if (getMut('mouseDown')) {
      values.current[0] = lerp(values.current[0], (state.mouse.x * movement), toSpeed)
      values.current[1] = lerp(values.current[1], (state.mouse.y * movement), toSpeed)
      box.current.position.set((getMut('x')) + values.current[0], ((getMut('y')) + values.current[1]), 0);
    } else {
      values.current[0] = lerp(values.current[0], 0, backSpeed)
      values.current[1] = lerp(values.current[1], 0, backSpeed)
      box.current.position.set((getMut('x')) + values.current[0], ((getMut('y')) + values.current[1]), 0);
    }
    box.current.scale.set(1 + Math.abs(getMut('x') * 0.3), 1 + Math.abs(getMut('y') * 0.3), 1);
  })
  let offset = props.size
  let smallOffset = 0.3

  return (
    <group ref={box}>
      <Wall {...props} index={0} position={[0, offset, back]} rotation={[Math.PI / 2, 0, 0]} />
      <Wall {...props} index={1} position={[0, -offset, back]} rotation={[Math.PI / 2, 0, 0]} />
      <Wall {...props} index={2} position={[offset, 0, back]} rotation={[Math.PI / 2, Math.PI / 2, 0]} />
      <Wall {...props} index={3} position={[-offset, 0, back]} rotation={[Math.PI / 2, Math.PI / 2, 0]} />

      <SmallWall {...props} index={0} position={[0, offset - smallOffset, back2]} rotation={[Math.PI / 2, 0, 0]} />
      <SmallWall {...props} index={1} position={[0, -offset + smallOffset, back2]} rotation={[Math.PI / 2, 0, 0]} />
      <SmallWall {...props} index={2} position={[offset - smallOffset, 0, back2]} rotation={[Math.PI / 2, Math.PI / 2, 0]} />
      <SmallWall {...props} index={3} position={[-offset + smallOffset, 0, back2]} rotation={[Math.PI / 2, Math.PI / 2, 0]} />

      <Wall {...props} index={0} position={[-offset, offset, back]} rotation={[Math.PI / 2, 0, 0]} />
      <Wall {...props} index={1} position={[offset, -offset, back]} rotation={[Math.PI / 2, 0, 0]} />
      <Wall {...props} index={2} position={[offset, offset, back]} rotation={[Math.PI / 2, Math.PI / 2, 0]} />
      <Wall {...props} index={3} position={[-offset, -offset, back]} rotation={[Math.PI / 2, Math.PI / 2, 0]} />

      <Wall {...props} index={0} position={[-offset * 2, offset, back]} rotation={[Math.PI / 2, 0, 0]} />
      <Wall {...props} index={0} position={[-offset * 2, 0, back]} rotation={[Math.PI / 2, 0, 0]} />
      <Wall {...props} index={0} position={[-offset * 2, -offset, back]} rotation={[Math.PI / 2, 0, 0]} />

      <Wall {...props} index={0} position={[offset * 2, offset, back]} rotation={[Math.PI / 2, 0, 0]} />
      <Wall {...props} index={0} position={[offset * 2, 0, back]} rotation={[Math.PI / 2, 0, 0]} />
      <Wall {...props} index={0} position={[offset * 2, -offset, back]} rotation={[Math.PI / 2, 0, 0]} />
    </group>
  )
}

function Wall({ color, size, ...props }) {
  const [meshz, setMesh] = useState()
  const texture = useTexture('./tiles/floor_tiles_09_ao_1k.jpg')
  const texture2 = useTexture('./tiles/floor_tiles_09_ao_1k-2.jpg')
  useEffect(() => {
    let rep = 0.5
    let rep2 = 1

    texture.wrapS = texture.wrapT = THREE.RepeatWrapping
    texture2.wrapS = texture2.wrapT = THREE.RepeatWrapping

    texture.repeat.set(rep * 2, rep);
    texture2.repeat.set(rep2, rep2);

    let geo = new THREE.BoxGeometry(size, size * 0.5, size);
    let mat1 = new THREE.MeshStandardMaterial({ map: texture, emissive: 'navy', normalScale: 1 })
    let mat2 = new THREE.MeshStandardMaterial({ map: texture2, emissive: 'navy', normalScale: 1 })

    let mesh = new THREE.Mesh(geo, [mat1, mat1, mat2, mat1, mat1, mat1])
    setMesh(mesh)
  }, [texture, texture2])

  return (
    (meshz) ? <primitive object={meshz} receiveShadow {...props} /> : <></>
  )
}

function SmallWall({ color, size, ...props }) {
  const [meshz, setMesh] = useState()
  const texture = useTexture('tiles/floor_tiles_09_ao_1k.jpg')
  const texture2 = useTexture('tiles/floor_tiles_09_ao_1k-2.jpg')
  useEffect(() => {
    let rep = 0.3
    let rep2 = 1

    texture.wrapS = texture.wrapT = THREE.RepeatWrapping
    texture2.wrapS = texture2.wrapT = THREE.RepeatWrapping

    texture.repeat.set(rep * 20, rep);
    texture2.repeat.set(rep2, rep2);

    let geo = new THREE.BoxGeometry(size, size * 0.1, size);
    let mat1 = new THREE.MeshStandardMaterial({ map: texture, emissive: 'navy' })
    let mat2 = new THREE.MeshStandardMaterial({ map: texture2, emissive: 'blue' })

    let mesh = new THREE.Mesh(geo, [mat1, mat1, mat2, mat1, mat1, mat1])
    setMesh(mesh)
  }, [texture, texture2])

  return (
    (meshz) ? <primitive object={meshz} receiveShadow {...props} /> : <></>
  )
}


function Box({ color, size, index, ...props }) {
  const { getMut } = useStore(state => state.api)
  const { pong } = useStore(state => state.api)
  const rat = 1.05
  const [ref, api] = useBox(() => ({ type: "Kinematic", ...props, mass: 0, args: [size * rat, size * rat, size * rat * 0.75], onCollide: e => pong(e.contact.impactVelocity, index) }))
  let values = useRef([0, 0, 0, 0])

  useFrame(state => {
    if (getMut('mouseDown')) {
      values.current[0] = lerp(values.current[0], (state.mouse.x * movement), toSpeed)
      values.current[1] = lerp(values.current[1], (state.mouse.y * movement), toSpeed)
      api.position.set(values.current[0] + props.position[0], values.current[1] + props.position[1], 0)
    } else {
      values.current[0] = lerp(values.current[0], 0, backSpeed)
      values.current[1] = lerp(values.current[1], 0, backSpeed)
      api.position.set(values.current[0] + props.position[0], values.current[1] + props.position[1], 0)
    }
  })
  return (
    <mesh ref={ref} {...props} receiveShadow>
      <boxBufferGeometry attach="geometry" args={[size, size, size]} />
      <meshBasicMaterial visible={false} attach="material" sides={THREE.DoubleSide} />
    </mesh>
  )
}

function Models() {
  const mutation = useStore(state => state.mutation)
  useFrame(state => {
    mutation.models.forEach((model) => {
      let curr = model.ref.current
      if (Math.abs(curr.position.x) > 30 || Math.abs(curr.position.y) > 30) {
        let size = 5
        let s = model.scale
        let rs = s * 2
        curr.scale.set(Math.random() * rs, Math.random() * rs, 0)
        TweenMax.to(curr.scale, 1, { x: s, y: s, z: s })
        model.api.position.set((-size * 0.5) + (Math.random() * size), (-size * 0.5) + (Math.random() * size), 0)
        model.api.angularVelocity.set(0, 0, 0)
        model.api.velocity.set(0, 0, 0)
      }
    })
  })
  let p = 1.15
  return (
    <>
      <Plant position={[-p+ (Math.random()*0.3), p, 0]} scale={5} />
      <Globe position={[-p + (Math.random()*0.3), -p, 0]}  scale={2} />
      <Bananas position={[p+ (Math.random()*0.3), p, 0]} scale={3.2} rotation={[0, Math.PI * 1.5, 0]} />
      <Lamp position={[p+ (Math.random()*0.3), -p, 0]} scale={0.165} rotation={[0, -0.5, 0]} color={'orange'} />
    </>
  )
}
function Paddle() {
  const si = 5
  return (
    <mesh dispose={null}>
      <group>
        <Box index={0} position={[0, si, 0]} size={si} color={niceColors[col][1]} />
        <Box index={1} position={[si, 0, 0]} size={si} color={niceColors[col][2]} />
        <Box index={2} position={[-si, 0, 0]} size={si} color={niceColors[col][3]} />
        <Box index={3} position={[0, -si, 0]} size={si} color={niceColors[col][4]} />
        <InsideBox size={si} color={"#fff"} index={0} />
      </group>
    </mesh>
  )
}

function Rig() {
  const { camera, mouse } = useThree()
  const vec = new THREE.Vector3()
  return useFrame(() => {
    camera.position.lerp(vec.set(-mouse.x * 1, -mouse.y * 0.2, camera.position.z), 0.1)
    camera.lookAt(0, 0, 0)

  })
}


function Swarm({ count }) {
  const { getMut } = useStore(state => state.api)

  const mesh = useRef()
  const dummy = useMemo(() => new THREE.Object3D(), [])

  const particles = useMemo(() => {
    const temp = []
    for (let i = 0; i < count; i++) {
      const t = Math.random() * 100
      const factor = 20 + Math.random() * 100
      const speed = 0.01 + Math.random() / 200
      const xFactor = -20 + Math.random() * 40
      const yFactor = -20 + Math.random() * 20
      const zFactor = -30 + Math.random() * 60
      temp.push({ t, factor, speed, xFactor, yFactor, zFactor, mx: 0, my: 0 })
    }
    return temp
  }, [count])

  const mult = 0.1;
  useFrame(state => {
    particles.forEach((particle, i) => {
      let { t, factor, speed, xFactor, yFactor, zFactor } = particle
      t = particle.t += (speed / 4) + (getMut('s') / 15)
      const a = Math.cos(t) + Math.sin(t * 1) / 10
      const b = Math.sin(t) + Math.cos(t * 2) / 10
      const s = Math.max(0.5, Math.cos(t) * 3.3)
      particle.mx += ((state.mouse.x * (window.innerWidth * mult)) - particle.mx) * 0.02
      particle.my += (-state.mouse.y * (window.innerHeight * mult) - particle.my) * 0.02
      dummy.position.set(
        (particle.mx / 10) * a + xFactor + Math.cos((t / 10) * factor) + (Math.sin(t * 1) * factor) / 10,
        5 + (particle.my / 10) * b + yFactor + Math.sin((t / 10) * factor) + (Math.cos(t * 2) * factor) / 10,
        -90 + (particle.my / 10) * b + zFactor + Math.cos((t / 10) * factor) + (Math.sin(t * 3) * factor) / 10
      )
      dummy.scale.set(s, s, s)
      dummy.updateMatrix()
      mesh.current.setMatrixAt(i, dummy.matrix)
    })
    mesh.current.instanceMatrix.needsUpdate = true
  })

  return (
    <>
      <instancedMesh ref={mesh} args={[null, null, count]}>
        <sphereBufferGeometry attach="geometry" args={[0.3, 16, 16]} />
        <meshStandardMaterial attach="material" color="lightblue"
          emissive="navy"
          transparent={true}
          opacity={0.6}
        />
      </instancedMesh>
    </>
  )
}

export default function () {
  const { start, stop } = useStore(state => state.api)
  const mutation = useStore(state => state.mutation)

  useEffect(() => {
    //window.addEventListener ("resize", updateAspect);
  }, [])

  const mouseUp = useCallback(() => {
    document.body.style.cursor = "grab"
    mutation.mouseDown = false

    stop()
  }, [])

  const mouseDown = useCallback(() => {
    start()
    mutation.mouseDown = true
    document.body.style.cursor = "grabbing"
  }, [])

  const mouseMove = useCallback((e) => {

    if (e.buttons === 0) {
      stop(true)
      mutation.mouseDown = false
      document.body.style.cursor = "grab"
    }

  }, [])
  let aspect = window.innerWidth / window.innerHeight
  return (
    <>
      <Canvas shadowMap camera={{ position: [0, 0, 10 - (aspect * 3)], fov: 60, far: 200 }}

        onMouseMove={mouseMove}
        onMouseDown={mouseDown}
        onMouseUp={mouseUp}

        onPointerDown={mouseDown}
        onPointerUp={mouseUp}

        gl={{ alpha: true, antialias: false }}
        pixelRatio={0.3 * window.devicePixelRatio}
        onCreated={({ gl }) => {
          gl.toneMapping = THREE.ACESFilmicToneMapping
          gl.outputEncoding = THREE.sRGBEncoding
        }}
      >
        <ambientLight intensity={0.4} />
        <spotLight
          position={[10, 10, 10]}
          angle={0.2}
          penumbra={1}
          intensity={0.2}
        />
        <Physics
          iterations={20}
          tolerance={0.00001}
          defaultContactMaterial={{
            friction: 0.8,
            restitution: 0.7, //0.7
            contactEquationStiffness: 1e8,
            contactEquationRelaxation: 0.9,
            frictionEquationStiffness: 1e7,
            frictionEquationRelaxation: 2,
          }}
          gravity={[0, -2, 0]}
          allowSleep={false}
        >
          <Suspense fallback={null}>
            <Paddle />
            <Swarm count={600} />
          </Suspense>
          <Models />
        </Physics>
        <Rig />
        <directionalLight position={[-1, 4, -2]} intensity={2} color="red" />
        <pointLight position={[-1, -4, -0]} intensity={3} color="green" />
      </Canvas>
    </>
  )
}

console.log('%c🌱🌱 @ramioso 2021-05-18 🌱🌱 ', 'color: orange');